import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { buildProductCode } from 'helpers/utils/util';
import { Cart } from 'types/entity/cart';
import { Money } from 'types/entity/product';

export const hasP2RDistributionChannel = (carts: any[]): boolean =>
  Array.isArray(carts) &&
  carts.some(
    (cart) =>
      Array.isArray(cart?.lineItems) &&
      cart.lineItems.some(
        (li: any) =>
          typeof li?.distributionChannel?.key === 'string' && li.distributionChannel.key.toLowerCase().includes('p2r'),
      ),
  );

const isP2RKey = (s?: string): boolean => typeof s === 'string' && s.toLowerCase().includes('p2r');

export type AvailabilityLineItem = {
  productCode: string;
  quantity: number;
  mediaFileCount: number;
};

export type CartAvailabilityRequest = {
  cartId: string;
  storeInfo: { fulfillerId?: string; storeNumber?: string };
  lineItems: AvailabilityLineItem[];
};

export const buildAvailabilityLineItems = (cart?: Cart): AvailabilityLineItem[] =>
  (cart?.lineItems ?? [])
    .filter((li) => isP2RKey(li?.distributionChannel?.key))
    .map((li) => {
      const attrs = li?.variant?.attributes ?? {};
      const override =
        typeof (attrs as any)?.product_code_override_universal === 'string'
          ? (attrs as any).product_code_override_universal
          : undefined;
      const prefix =
        typeof (attrs as any)?.product_code_prefix === 'string' ? (attrs as any).product_code_prefix : undefined;
      const itemId =
        typeof (attrs as any)?.product_item_id_universal === 'string'
          ? (attrs as any).product_item_id_universal
          : undefined;

      const productCode = buildProductCode(override, prefix, itemId);

      if (!productCode) return null;
      const quantity = Math.max(1, li?.count ?? 1);

      const mediaFileCount = li?.mediaCount ?? 1;

      return { productCode, quantity, mediaFileCount };
    })
    .filter((x): x is AvailabilityLineItem => x !== null);

/**
 * Builds one availability request per P2R cart so that a cart's timestamp request
 * never mixes line items belonging to another cart (M2H carts are skipped).
 */
export const buildCartAvailabilityRequests = (carts: Cart[] = []): CartAvailabilityRequest[] =>
  getP2RCarts(carts ?? []).map((cart) => ({
    cartId: cart?.cartId,
    storeInfo: {
      fulfillerId: cart?.shippingInfo?.fulfillerId,
      storeNumber: cart?.shippingInfo?.storeInfo,
    },
    lineItems: buildAvailabilityLineItems(cart),
  }));

export const getP2RCarts = <T extends { lineItems?: any[] }>(carts: T[]): T[] =>
  Array.isArray(carts)
    ? carts.filter(
        (cart) =>
          Array.isArray(cart?.lineItems) &&
          cart.lineItems.some(
            (li: any) =>
              typeof li?.distributionChannel?.key === 'string' &&
              li.distributionChannel.key.toLowerCase().includes('p2r'),
          ),
      )
    : [];

export const formatMoney = (m?: Money, locale = 'en-US'): string => CurrencyHelpers.formatForCurrency(m ?? {}, locale);

export const channelConfigForCart = {
  m2hKeys: ['m2h-online-us', 'm2h-online-ca', 'm2h-online-eu'],
  p2rKeys: ['p2r-walmart-us', 'p2r-walgreens-us', 'p2r-cvs-us'],
} as const;

export const isM2H = (shippingKey?: any): boolean =>
  !!shippingKey && channelConfigForCart.m2hKeys.includes(shippingKey);

export const isP2R = (shippingKey?: any): boolean =>
  !!shippingKey && channelConfigForCart.p2rKeys.includes(shippingKey);

const P2R_STORE_KEYS = new Set(['p2r-walmart-us', 'p2r-walgreens-us', 'p2r-cvs-us']);
export const getFulfillmentFromShippingKey = (
  shippingKey: string,
): {
  icon: 'truck' | 'store';
  showPriceAsterisk: boolean;
  isTaxText?: boolean;
  isM2H?: boolean;
  isP2R?: boolean;
} => {
  const key = (shippingKey ?? '').toLowerCase();
  const p2r = Boolean(isP2R(key));
  const m2h = Boolean(isM2H(key));

  if (m2h) {
    return {
      icon: 'truck',
      showPriceAsterisk: false,
      isTaxText: p2r,
      isM2H: m2h,
      isP2R: false,
    };
  }

  if (P2R_STORE_KEYS.has(key)) {
    return {
      icon: 'store',
      showPriceAsterisk: true,
      isTaxText: p2r,
      isP2R: p2r,
      isM2H: false,
    };
  }

  return {
    icon: p2r ? 'store' : 'truck',
    showPriceAsterisk: p2r,
    isTaxText: p2r,
  };
};
